/** Bidirectional PSTN via Twilio SIP → xAI Voice Agent. Not Chat+TTS. */

import {
  callPhoneForContactMode,
  hydrateMeganContactMode,
  type MeganContactMode,
} from "./contactMode";

export type { MeganContactMode };

export const XAI_SIP_HOST = "sip.voice.x.ai";
export const XAI_PHONE_NUMBERS_URL = "https://api.x.ai/v1/phone-numbers";
export const XAI_PHONE_NUMBERS_URL_V2 = "https://api.x.ai/v2/phone-numbers";
export const TWILIO_CALLS_URL = "https://api.twilio.com/2010-04-01/Accounts";

export const PHONE_UNAVAILABLE = "Phone unavailable — using the desk.";
export const PHONE_CALLING = "Calling phone…";
export const PHONE_ANSWERED = "Phone answered — Voice Agent on the PSTN circuit.";
export const PHONE_FAILED = "Phone call failed — using the desk.";

export type PhoneCallPhase = "idle" | "calling" | "ringing" | "answered" | "failed" | "ended" | "unavailable";
export type PhoneCallDirection = "inbound" | "outbound";

/** Local Settings + optional Nanode env. Secrets never committed. */
export type PhoneSettings = {
  /** Duty Standby rings Emerson’s cell when a new job lands. Derived from contactMode. */
  callPhone: boolean;
  /** prefer text / prefer call / force text / force call / off. */
  contactMode: MeganContactMode;
  /** Emerson’s E.164 cell. TODO: distinguish Emerson vs other callers by caller ID (not a multi-crew phonebook here). */
  toNumber: string;
  /** Twilio caller ID / from-number (E.164). */
  fromNumber: string;
  /** Optional dedicated Megan SMS/RCS from-number. Defaults to fromNumber. */
  textFromNumber: string;
  /** Optional dedicated inbound dispatcher DID. Defaults to fromNumber. */
  dispatcherDid: string;
  twilioAccountSid: string;
  twilioAuthToken: string;
  /**
   * Extra inbound E.164 numbers Megan will answer besides Emerson’s Duty Standby cell.
   * Unknown callers are refused. Emerson’s toNumber / env / crew phone is always allowed.
   */
  inboundAllowlist: string[];
};

export const DEFAULT_PHONE_SETTINGS: PhoneSettings = {
  callPhone: false,
  contactMode: "prefer_text",
  toNumber: "",
  fromNumber: "",
  textFromNumber: "",
  dispatcherDid: "",
  twilioAccountSid: "",
  twilioAuthToken: "",
  inboundAllowlist: [],
};

export function hydratePhoneSettings(raw?: Partial<PhoneSettings> | null): PhoneSettings {
  const contactMode = hydrateMeganContactMode(raw?.contactMode, raw?.callPhone);
  const explicitCall = raw != null && typeof raw.callPhone === "boolean";
  return {
    callPhone: explicitCall ? Boolean(raw.callPhone) : callPhoneForContactMode(contactMode),
    contactMode,
    toNumber: typeof raw?.toNumber === "string" ? raw.toNumber.trim() : "",
    fromNumber: typeof raw?.fromNumber === "string" ? raw.fromNumber.trim() : "",
    textFromNumber: typeof raw?.textFromNumber === "string" ? raw.textFromNumber.trim() : "",
    dispatcherDid: typeof raw?.dispatcherDid === "string" ? raw.dispatcherDid.trim() : "",
    twilioAccountSid: typeof raw?.twilioAccountSid === "string" ? raw.twilioAccountSid.trim() : "",
    twilioAuthToken: typeof raw?.twilioAuthToken === "string" ? raw.twilioAuthToken.trim() : "",
    inboundAllowlist: uniqueE164List(raw?.inboundAllowlist),
  };
}

export function redactPhoneSettings(settings: PhoneSettings): PhoneSettings {
  return {
    ...settings,
    twilioAccountSid: "",
    twilioAuthToken: "",
  };
}

/** Normalize to +E.164 or empty. Accepts 10-digit NANP. */
export function normalizeE164(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const digits = trimmed.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) {
    const rest = digits.slice(1).replace(/\D/g, "");
    return rest.length >= 8 && rest.length <= 15 ? `+${rest}` : "";
  }
  const only = digits.replace(/\D/g, "");
  if (only.length === 11 && only.startsWith("1")) return `+${only}`;
  if (only.length === 10) return `+1${only}`;
  if (only.length >= 8 && only.length <= 15) return `+${only}`;
  return "";
}

export function isValidE164(raw: string): boolean {
  return /^\+[1-9]\d{7,14}$/.test(normalizeE164(raw) || raw.trim());
}

/** Deduped E.164 list. Accepts an array or a comma/space-separated string (Nanode env). */
export function uniqueE164List(raw?: readonly string[] | string | null): string[] {
  const items = Array.isArray(raw)
    ? raw
    : typeof raw === "string"
      ? raw.split(/[,\s]+/)
      : [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const n = normalizeE164(typeof item === "string" ? item : "");
    if (!n || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

export type MeganInboundCrewRow = {
  id?: string;
  username?: string;
  displayName?: string;
  phone?: string;
};

/** Emerson’s crew card — not every crew phone. Match id / username / given name only. */
export function isEmersonCrewRow(row?: MeganInboundCrewRow | null): boolean {
  if (!row) return false;
  const id = String(row.id ?? "").trim().toLowerCase();
  const user = String(row.username ?? "").trim().toLowerCase();
  const name = String(row.displayName ?? "").trim().toLowerCase();
  return id === "emerson" || user === "emerson" || name === "emerson" || name.startsWith("emerson ");
}

export function emersonCrewPhone(crew?: readonly MeganInboundCrewRow[] | null): string {
  const hit = Array.isArray(crew) ? crew.find(isEmersonCrewRow) : undefined;
  return normalizeE164(hit?.phone ?? "");
}

/** Duty Standby cell: Settings toNumber, then EMERSON_TO_NUMBER, then Emerson’s crew phone. Never a hardcoded DID. */
export function emersonDutyNumber(input: {
  toNumber?: string;
  envToNumber?: string;
  crew?: readonly MeganInboundCrewRow[] | null;
} = {}): string {
  return (
    normalizeE164(input.toNumber ?? "") ||
    normalizeE164(input.envToNumber ?? "") ||
    emersonCrewPhone(input.crew) ||
    ""
  );
}

export type MeganInboundAllowInput = {
  toNumber?: string;
  envToNumber?: string;
  inboundAllowlist?: readonly string[] | string | null;
  crew?: readonly MeganInboundCrewRow[] | null;
};

/** Emerson’s Duty Standby cell plus the Settings allowlist. Other crew phones stay off until added. */
export function inboundAllowedNumbers(input: MeganInboundAllowInput = {}): string[] {
  return uniqueE164List([
    emersonDutyNumber(input),
    ...uniqueE164List(input.inboundAllowlist),
  ]);
}

export function isInboundCallerAllowed(from: string, input: MeganInboundAllowInput = {}): boolean {
  const want = normalizeE164(from);
  return Boolean(want) && inboundAllowedNumbers(input).includes(want);
}

export function isPhoneSessionLive(phase: PhoneCallPhase): boolean {
  return phase === "calling" || phase === "ringing" || phase === "answered";
}

export type MeganInboundGate =
  | { allow: true }
  | { allow: false; reason: "unknown" | "busy" };

/**
 * One Megan only. Unknown From is refused. A live session that is not this
 * outbound answer / same SIP callId is busy.
 */
export function gateMeganInbound(input: MeganInboundAllowInput & {
  from: string;
  live: boolean;
  sameSession?: boolean;
}): MeganInboundGate {
  if (input.sameSession) return { allow: true };
  if (input.live) return { allow: false, reason: "busy" };
  if (!isInboundCallerAllowed(input.from, input)) return { allow: false, reason: "unknown" };
  return { allow: true };
}

/** Matches the existing TwiML refuse style (`Say` + `Hangup`), Celtic desk voice. */
export const MEGAN_INBOUND_REFUSE_SAY = "Celtic Dispatch. Sorry — this line is for company crew only.";

export function twimlInboundRefuse(): string {
  return `<?xml version="1.0" encoding="UTF-8"?><Response><Say>${escapeXml(MEGAN_INBOUND_REFUSE_SAY)}</Say><Hangup/></Response>`;
}

export function twimlInboundBusy(): string {
  return `<?xml version="1.0" encoding="UTF-8"?><Response><Reject reason="busy"/></Response>`;
}

export function dispatcherDidOf(settings: PhoneSettings): string {
  return normalizeE164(settings.dispatcherDid) || normalizeE164(settings.fromNumber);
}

/** Documented xAI SIP URI: sip:{number}@sip.voice.x.ai;transport=tls */
export function xaiSipUri(e164: string, host = XAI_SIP_HOST): string {
  const number = normalizeE164(e164);
  if (!number) return "";
  return `sip:${number}@${host};transport=tls`;
}

export function twimlSipBridge(e164: string, host = XAI_SIP_HOST): string {
  const uri = xaiSipUri(e164, host);
  if (!uri) {
    return `<?xml version="1.0" encoding="UTF-8"?><Response><Say>Dispatch phone is not configured.</Say><Hangup/></Response>`;
  }
  return [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<Response>`,
    `<Dial answerOnBridge="true">`,
    `<Sip>${escapeXml(uri)}</Sip>`,
    `</Dial>`,
    `</Response>`,
  ].join("");
}

function escapeXml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function phoneSettingsHaveTwilio(settings: PhoneSettings): boolean {
  return Boolean(
    settings.twilioAccountSid.trim() &&
      settings.twilioAuthToken.trim() &&
      normalizeE164(settings.fromNumber),
  );
}

export function phoneCanDial(settings: PhoneSettings): boolean {
  return settings.callPhone && Boolean(normalizeE164(settings.toNumber)) && phoneSettingsHaveTwilio(settings);
}

export function phoneUnavailableReason(settings: PhoneSettings, syncConfigured: boolean): string {
  if (!settings.callPhone) return PHONE_UNAVAILABLE;
  if (!normalizeE164(settings.toNumber)) return "Phone unavailable — set Emerson’s E.164 number.";
  if (!phoneSettingsHaveTwilio(settings) && !syncConfigured) {
    return "Phone unavailable — Twilio creds and Nanode sync URL required.";
  }
  if (!syncConfigured) return "Phone unavailable — point Data / Sync at Nanode for webhooks.";
  return "";
}

export function phoneCallPhaseLabel(phase: PhoneCallPhase): string {
  switch (phase) {
    case "calling":
      return PHONE_CALLING;
    case "ringing":
      return "Phone ringing…";
    case "answered":
      return PHONE_ANSWERED;
    case "failed":
      return PHONE_FAILED;
    case "ended":
      return "Phone hung up.";
    case "unavailable":
      return PHONE_UNAVAILABLE;
    default:
      return "Phone idle.";
  }
}

export type PhoneBridgeStatus = {
  phase: PhoneCallPhase;
  direction: PhoneCallDirection | null;
  detail: string;
  from: string;
  to: string;
  twilioConfigured: boolean;
  xaiConfigured: boolean;
  publicBase: boolean;
};

export const IDLE_PHONE_STATUS: PhoneBridgeStatus = {
  phase: "idle",
  direction: null,
  detail: "",
  from: "",
  to: "",
  twilioConfigured: false,
  xaiConfigured: false,
  publicBase: false,
};
